import type { Tenant } from './database.types';

/**
 * Logique PURE du statut de site client + export CSV de la liste des restos.
 * Aucune dépendance réseau/DOM → testable directement.
 * L'écriture Supabase est dans siteStatusService.ts.
 */

export type SiteStatus = 'active' | 'suspended' | 'disabled';

export const SITE_STATUSES: SiteStatus[] = ['active', 'suspended', 'disabled'];

export const SITE_STATUS_LABELS: Record<SiteStatus, string> = {
  active: 'Actif',
  suspended: 'Suspendu',
  disabled: 'Désactivé',
};

/** Statut effectif d'un resto (défaut 'active' si absent). */
export function siteStatusOf(tenant: Pick<Tenant, 'site_status'>): SiteStatus {
  const s = tenant.site_status;
  return s === 'suspended' || s === 'disabled' ? s : 'active';
}

/** Le site est-il en ligne (accessible aux clients) ? */
export function isSiteActive(tenant: Pick<Tenant, 'site_status'>): boolean {
  return siteStatusOf(tenant) === 'active';
}

/** Date d'activation lisible (jj/mm/aaaa) à partir de created_at. '—' si absent. */
export function activationDate(created_at?: string | null): string {
  if (!created_at) return '—';
  const d = new Date(created_at);
  if (isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Échappe une valeur pour le CSV (guillemets + séparateur). */
function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Construit un CSV de la liste des restaurants (séparateur `;`, compatible Excel FR).
 * Colonnes : Nom, Adresse web, Ville, Statut site, Facturation, Activé le, Email gérant.
 */
export function buildClientsCsv(tenants: Tenant[]): string {
  const header = ['Nom', 'Adresse web', 'Ville', 'Statut site', 'Facturation', 'Activé le', 'Email gérant'];
  const lines = tenants.map((t) =>
    [
      t.name,
      `/r/${t.slug}`,
      t.city ?? '',
      SITE_STATUS_LABELS[siteStatusOf(t)],
      t.billing_status ?? 'none',
      activationDate(t.created_at),
      t.manager_email ?? t.email ?? '',
    ]
      .map(csvCell)
      .join(';'),
  );
  return [header.join(';'), ...lines].join('\n');
}
